"use client"

import { cn } from "@coval/ui/cn"
import { ChevronDown } from "lucide-react"

/**
 * A sidebar section's label, and the control that folds the section away.
 *
 * The heading is the button rather than a chevron beside it: the label is what
 * names the rows being folded, so it is the thing to press, and it keeps the
 * section's title on the same margin whether the section is open or shut.
 *
 * `mt-7` is the gap a section owns above itself, and every section label in
 * this sidebar — contributed ones included — carries the same one.
 *
 * The chevron stays invisible until the row is hovered — a nav at rest should
 * read as labels, not as a column of controls — and stays out once the section
 * is closed, where it is the only thing distinguishing a folded section from
 * one that simply has nothing in it.
 */
export function SidebarSectionHeading({
  title,
  expanded,
  onToggle,
}: {
  title: string
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <h2 className="mt-7 flex items-center px-2 pb-1">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="group/label flex w-full items-center gap-1 text-left text-xs font-semibold text-coval-text-muted opacity-50 transition-opacity hover:opacity-100"
      >
        <span className="min-w-0 truncate">{title}</span>
        <ChevronDown
          aria-hidden
          className={cn(
            "size-3 shrink-0 transition-transform",
            expanded ? "opacity-0 group-hover/label:opacity-100" : "-rotate-90",
          )}
        />
      </button>
    </h2>
  )
}
